import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type {
  AgencyResponse,
  UserManagerUserResponse,
} from '../../../services/api/types.gen';
import type { UserRolesState } from '../types';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  EditUserInfoSection,
  type EditUserInfoHandle,
} from '../../../components/rbac/role-assignment-dialog/EditUserInfoSection';

export interface EditUserRolesDialogProps {
  open: boolean;
  agency: AgencyResponse | null;
  user: UserManagerUserResponse | null;
  rolesState: UserRolesState;
  onClose: () => void;
  onSave: (roleIds: string[]) => Promise<void>;
}

const EditUserRolesDialog: React.FC<EditUserRolesDialogProps> = ({
  open,
  agency,
  user,
  rolesState,
  onClose,
  onSave,
}) => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );
  const isSuperUser = hasPermission('admin:super-user');

  const editUserInfoRef = useRef<EditUserInfoHandle>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(
    new Set()
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (rolesState.roles) {
      setSelectedRoleIds(new Set(rolesState.roles.map(r => r.role_id)));
    }
  }, [rolesState.roles]);

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isSuperUser && user && editUserInfoRef.current) {
        try {
          await editUserInfoRef.current.saveIfDirty();
        } catch {
          // Error already shown by EditUserInfoSection; stop here
          return;
        }
      }
      // onSave handles its own success toast, error toast, and dialog close
      await onSave(Array.from(selectedRoleIds));
    } finally {
      setIsSaving(false);
    }
  };

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
    : '';

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Edit roles</DialogTitle>
          {user && agency && (
            <p className='text-sm text-muted-foreground mt-1'>
              <span className='font-medium text-foreground'>{fullName}</span>
              {' at '}
              <span className='font-medium text-foreground'>
                {agency.written_name}
              </span>
            </p>
          )}
        </DialogHeader>

        {isSuperUser && user && (
          <EditUserInfoSection
            ref={editUserInfoRef}
            userId={user.id}
            firstName={user.first_name}
            lastName={user.last_name}
            phoneNumber={user.phone_number}
            isRogerEmployee={user.is_roger_employee ?? false}
          />
        )}

        <div className='py-2'>
          {rolesState.isLoading ? (
            <div className='flex items-center justify-center py-6 gap-2 text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span className='text-sm'>Loading roles…</span>
            </div>
          ) : (
            <div className='space-y-1 max-h-72 overflow-y-auto'>
              {rolesState.availableRoles?.map(role => {
                const isSelected = selectedRoleIds.has(role.id);
                return (
                  <button
                    type='button'
                    key={role.id}
                    onClick={() => toggleRole(role.id)}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
                      isSelected
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                  >
                    <div
                      className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                        isSelected
                          ? 'bg-primary border-primary'
                          : 'border-input bg-background'
                      }`}
                    >
                      {isSelected && (
                        <svg
                          className='w-2.5 h-2.5 text-primary-foreground'
                          viewBox='0 0 10 10'
                          fill='none'
                        >
                          <path
                            d='M1.5 5L4 7.5L8.5 2.5'
                            stroke='currentColor'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm font-medium leading-tight'>
                        {role.name}
                      </p>
                      {role.description && (
                        <p className='text-xs text-muted-foreground mt-0.5 line-clamp-1'>
                          {role.description}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
              {rolesState.availableRoles?.length === 0 && (
                <p className='text-sm text-muted-foreground text-center py-4'>
                  No roles available for this agency.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={handleSave}
            disabled={isSaving || rolesState.isLoading}
          >
            {isSaving && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserRolesDialog;
